import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LogConsole } from './log-console-publisher';
import { LogLocalStorage } from './log-local-storage-publisher';
import { LogPublisher } from './log-publishers';
import { LogWebApi } from './log-web-api-publisher';
import { LogEntry, LoggerConfig, LogLevel } from './log.model';

@Injectable()
export class LogService implements OnDestroy {

  // Public properties
  level: LogLevel = LogLevel.All;
  logWithDate = true;
  publishers: LogPublisher[] = [];

  destroy$ = new Subject<void>();

  constructor(private config: LoggerConfig, private http: HttpClient) {
    // Set all the publishers into the local array
    this.buildPublishers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  debug(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Debug, optionalParams);
  }

  info(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Info, optionalParams);
  }

  warn(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Warn, optionalParams);
  }

  error(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Error, optionalParams);
  }

  fatal(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Debug, optionalParams);
  }

  log(msg: any, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.All, optionalParams);
  }

  clear(): void {
    for (const logger of this.publishers) {
      logger.clear();
    }
  }

  private buildPublishers(): void {
    let logPub: LogPublisher;

    for (const pub of this.config.publisher.filter(p => p.isActive)) {
      switch (pub.loggerName.toLowerCase()) {
        case 'console':
          logPub = new LogConsole();
          break;
        case 'localstorage':
          logPub = new LogLocalStorage();
          break;
        case 'webapi':
          logPub = new LogWebApi(this.http);
          break;
      }

      // Set location, if any, of the logging
      logPub.location = pub.loggerLocation;
      // Add publisher to array
      this.publishers.push(logPub);
    }
  }

  private shouldLog(level: LogLevel): boolean {
    let ret = false;

    if (this.level !== LogLevel.Off && level >= this.level) {
      ret = true;
    }

    return ret;
  }

  private writeToLog(msg: string, level: LogLevel, params: any[]) {
    if (this.shouldLog(level)) {
      let caller = 'unknown!!!';
      const stack = new Error('caller:').stack;

      if (stack) {
        const stackCallers = stack.split('at');
        if (stackCallers.length > 3) {
          caller = stackCallers[3];
        }
      }

      const entry: LogEntry = new LogEntry();

      entry.message = `${msg} => [${caller}]`;
      entry.level = level;
      entry.extraInfo = params;
      entry.logWithDate = this.logWithDate;

      // Log the value to all publishers
      for (const logger of this.publishers) {
        logger.log(entry).pipe(takeUntil(this.destroy$)).subscribe();
      }
    }
  }
}
